import React, { useState } from "react";
import { X, FileBadge, CheckCircle } from "lucide-react";
import frameBg from "../../../../../Assets/FrameBGDashboard.png";
import { ADClosedTask } from "../Service/AllTaskService";
import NotificationAlert from "../../../../Alerts/NotificationAlert";
 import { useNavigate } from "react-router-dom";

const ADClosedTaskModal = ({ show, onClose, taskData, onClosed }) => {
  const [remark, setRemark] = useState("");
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(0);
   const navigate = useNavigate();

  if (!show || !taskData) return null;

  // API CALL
  const handleCloseTask = async () => {
    if (!remark.trim()) {
      setAlert({
        type: "error",
        message: "Please enter a remark before completing the task.",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        taskNo: taskData.taskNo,
        remark: remark,
        rating: rating
      };

      const response = await ADClosedTask(payload);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({
          type: "success",
          message: data.message,
        });

        onClosed();
        setRemark("");

        setTimeout(() => {
          onClose();
          setAlert(null);
        }, 3000);
      } else {
        setAlert({
          type: "error",
          message: data.message || "Failed to complete task",
        });
      }
    } catch (error) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to close task.",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    const d = new Date(date);
    return `${d.getDate()}-${d.toLocaleString("en-US", {
      month: "short",
    })}-${d.getFullYear()} ${d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  };
  
  const ratingOptions = [
  { value: 1, emoji: "☹️", label: "Very Poor" },
  { value: 2, emoji: "☹️", label: "Poor" },
  { value: 3, emoji: "😕", label: "Below Average" },
  { value: 4, emoji: "😐", label: "Fair" },
  { value: 5, emoji: "🙂", label: "Average" },
  { value: 6, emoji: "🙂", label: "Satisfactory" },
  { value: 7, emoji: "😊", label: "Good" },
  { value: 8, emoji: "😊", label: "Very Good" },
  { value: 9, emoji: "😀", label: "Great" },
  { value: 10, emoji: "😃", label: "Excellent" },
];


  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-end z-50 transition-opacity duration-300 opacity-100"
        onClick={onClose}
      >
        <div
          className="bg-white  w-full max-w-[37rem] h-full shadow-2xl border-l animate-slideFadeIn flex flex-col overflow-hidden rounded-l-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="px-5 py-4 border-b flex items-center justify-between text-white shadow-md bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${frameBg})` }}
          >
            <div className="flex flex-col">
              <h2 className="text-xl font-semibold tracking-wide">
                Close Task
              </h2>

              <span className="mt-2 bg-white/20 backdrop-blur px-3 py-1 rounded-full text-sm font-medium w-fit">
                #{taskData.taskNo}
              </span>
            </div>

            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition"
            >
              <X size={26} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Task Details */}
            <div className="bg-white border shadow-sm rounded-xl p-5 space-y-4 hover:shadow-md transition">
              <div className="flex items-center justify-between w-full">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FileBadge size={16} className="text-blue-600" />
                  Task Details
                </h3>

                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 flex items-center gap-1">
                  <CheckCircle size={14} />
                  Completed
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 text-[0.93rem] text-gray-700">
                <p>
                  <span className="font-semibold">Description:</span>{" "}
                  {taskData.description}
                </p>

                <p>
                  <span className="font-semibold">Assigned By:</span>{" "}
                  {taskData.assignedByEmail}
                </p>

                <p>
                  <span className="font-semibold">Project:</span>{" "}
                  {taskData.projectName}
                </p>

                <p>
                  <span className="font-semibold">Expected Complete Date:</span>{" "}
                  {formatDateTime(taskData.estimatedComplete)}
                </p>

                <p>
                  <span className="font-semibold">Created At:</span>{" "}
                  {formatDateTime(taskData.createdAt)}
                </p>

                <p>
                  <span className="font-semibold">Severity:</span>{" "}
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold w-fit
                    ${
                      taskData.severity === "High"
                        ? "bg-red-100 text-red-700"
                        : taskData.severity === "Moderate"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {taskData.severity}
                  </span>
                </p>
              </div>
            </div>

            {/* Remarks */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Completion Remarks <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={4}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add completion remark..."
                className="w-full p-3 border rounded-lg text-sm bg-white focus:outline-none 
                       focus:ring-2 focus:ring-blue-400 shadow-sm"
              ></textarea>
            </div>

            {/* Rating */}
            <div className="bg-white border shadow-sm rounded-xl p-5 hover:shadow-md transition">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Rating
              </label> 

              <div className="flex items-center gap-2 flex-wrap">
                {[...Array(10)].map((_, index) => {
                  const value = index + 1;

                  const getHoverColor = () => {
                    if (value <= 3) return "hover:bg-red-200";
                    if (value <= 7) return "hover:bg-yellow-200";
                    return "hover:bg-green-200";
                  };

                  const getActiveColor = () => {
                    if (rating <= 3) return "bg-red-600 text-white";
                    if (rating <= 7) return "bg-yellow-500 text-white";
                    return "bg-green-600 text-white";
                  };

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(value)}
                      aria-label={`Rate ${value}`}
                      className={`w-10 h-10 rounded-full text-lg flex items-center justify-center transition-all duration-200
                        ${
                          rating === value
                            ? `${getActiveColor()} shadow-md scale-100`
                            : `bg-gray-100 text-gray-700 ${getHoverColor()}`
                        }
                      `}
                    >
                      {ratingOptions[value - 1].emoji}
                    </button>
                  );
                })}
              </div>

              {rating > 0 && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="font-medium text-gray-700">Selected:</span>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1
                      ${
                        rating <= 3
                          ? "bg-red-100 text-red-700"
                          : rating <= 7
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-green-100 text-green-700"
                      }`
                    }
                  >
                  <span className="text-base">{ratingOptions[rating - 1].emoji}</span>
                    {rating}/10 •{" "}
                    {ratingOptions[rating - 1].label}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 items-center shadow-inner">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 shadow-sm transition"
            >
              Cancel
            </button>

            <button
              onClick={handleCloseTask}
              disabled={loading}
              className="px-6 py-2 text-sm bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white rounded-md shadow-md hover:opacity-90 transition"
            >
              {loading ? "Closing..." : "Close Task"}
            </button>
          </div>
        </div>

        {alert && (
          <NotificationAlert
            type={alert.type}
            message={alert.message}
            show={true}
            duration={4000}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
    </>
  );
};

export default ADClosedTaskModal;
